import { inject, Injectable } from "@angular/core";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import { NiceAsyncTypeaheadProvider, NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { lastValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "../../api.service";
import { SearchCoursesDto } from "../dto/search-courses.dto";
import { CoursesStatus } from "../enums/courses-status.enum";
import { Courses } from "../models/courses.model";

export interface CourseTypeaheadOptions {
    categoryIds?: number[];
    distributorIds?: number[];
    isFreeDemo?: boolean;
    ignoreIds?: number[];
    status?: CoursesStatus;
    subCategoryIds?: number[];
}

@Injectable()
export class CourseTypeaheadApi extends NiceAsyncTypeaheadProvider<Courses> {
    private readonly api = inject(ApiService);

    public readonly resource = "courses";

    private readonly pageSize = 20;

    public override async getById(id: number): Promise<Courses> {
        return lastValueFrom(this.api.courses.getById(id));
    }

    public override search(
        searchQuery: string,
        page: number,
        options?: CourseTypeaheadOptions
    ): Observable<NiceAsyncTypeaheadSearchResult<Courses>> {
        const dto: SearchCoursesDto = {
            searchQuery,
            page,
            pageSize: this.pageSize
        };

        if (isPropertyDefined(options, "categoryIds")) {
            dto.categoryIds = options.categoryIds;
        }

        if (isPropertyDefined(options, "distributorIds")) {
            dto.distributorIds = options.distributorIds;
        }

        if (isPropertyDefined(options, "ignoreIds")) {
            dto.ignoreIds = options.ignoreIds;
        }

        if (isPropertyDefined(options, "isFreeDemo")) {
            dto.isFreeDemo = options.isFreeDemo;
        }

        if (isPropertyDefined(options, "status")) {
            dto.status = options.status;
        }

        if (isPropertyDefined(options, "subCategoryIds")) {
            dto.subCategoryIds = options.subCategoryIds;
        }

        return this.api.courses.search(dto).pipe(
            map((items) => ({
                items,
                nextPage: items.length === this.pageSize ? page + 1 : null
            }))
        );
    }

    public override format(item: Courses): string {
        if (typeof item.name === "string") {
            return item.name;
        }

        return "";
    }
}
